import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { Popper } from '../Popper/Popper.js';
import { List } from '../List/List.js';
import { ListItem } from '../ListItem/ListItem.js';
import { Typography } from '../Typography/Typography.js';
import { Button } from '../Button/Button.js';

var _styles;

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var comboButtonClassNames = {
  popper: 'popper',
  menuPaper: 'menuPaper',
  listRoot: 'listRoot',
  listItem: 'listItem'
};
var styles = (_styles = {}, _defineProperty(_styles, comboButtonClassNames.popper, {
  zIndex: 'modal'
}), _defineProperty(_styles, comboButtonClassNames.menuPaper, {
  padding: 1,
  boxShadow: 2
}), _defineProperty(_styles, comboButtonClassNames.listRoot, {
  backgroundColor: '#f00',
  display: 'flex',
  flexDirection: 'column',
  padding: '0'
}), _defineProperty(_styles, comboButtonClassNames.listItem, {
  justifyContent: 'flex-start',
  width: 'auto',
  padding: '8px 0'
}), _styles);
var keyListeners = {
  tab: 'Tab',
  escape: 'Escape'
};

var getEnabledItemIndexes = function getEnabledItemIndexes(menuItems) {
  var firstIndex = menuItems.findIndex(function (menuItem) {
    return !menuItem.disabled;
  });
  var lastIndex = menuItems.reduce(function (enabledMatch, menuItem, index) {
    return menuItem.disabled ? enabledMatch : index;
  }, -1);
  return [firstIndex, lastIndex];
};

var ComboButton = function ComboButton(_ref) {
  var text = _ref.text,
      buttonProps = _ref.buttonProps,
      menuItems = _ref.menuItems,
      menuDataProp = _ref.menuDataProp,
      popperPosition = _ref.popperPosition,
      sxPopper = _ref.sxPopper;

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      open = _React$useState2[0],
      setOpen = _React$useState2[1];

  var menuListRef = React.useRef(null);
  var buttonRef = React.useRef(null);

  var _getEnabledItemIndexe = getEnabledItemIndexes(menuItems),
      _getEnabledItemIndexe2 = _slicedToArray(_getEnabledItemIndexe, 2),
      firstEnabledItemIndex = _getEnabledItemIndexe2[0],
      lastEnabledItemIndex = _getEnabledItemIndexe2[1];

  var mergedButtonProps = _objectSpread(_objectSpread({}, buttonProps), {}, {
    color: 'primary',
    icon: open ? 'ExpandLess' : 'ExpandMore',
    onClick: function onClick() {
      return setOpen(function (previousOpen) {
        return !previousOpen;
      });
    }
  });

  var handleClickAway = function handleClickAway() {
    return setOpen(false);
  };

  var handleKeyDown = function handleKeyDown(event, index) {
    var tab = keyListeners.tab,
        escape = keyListeners.escape;
    var key = event.key,
        shiftKey = event.shiftKey;
    var atBeginning = index === firstEnabledItemIndex;
    var atEnd = index === lastEnabledItemIndex;

    if (key === tab && menuListRef.current) {
      // Pressing shift + tab on the first item cycles focus to the last item
      if (shiftKey && atBeginning) {
        event.preventDefault();
        menuListRef.current.children[lastEnabledItemIndex].focus();
      } // Pressing tab on the last item cycles focus to the first item


      if (!shiftKey && atEnd) {
        event.preventDefault();
        menuListRef.current.children[firstEnabledItemIndex].focus();
      }
    }

    if (key === escape) {
      setOpen(false);

      if (buttonRef.current) {
        buttonRef.current.focus();
      }
    }
  };

  var handleBlur = function handleBlur(event) {
    return event.preventDefault();
  };

  var menu = /*#__PURE__*/React.createElement(List, {
    "data-selector": menuDataProp,
    component: "nav",
    sx: styles[comboButtonClassNames.listRoot],
    ref: menuListRef
  }, menuItems.map(function (_ref2, index) {
    var value = _ref2.value,
        itemAction = _ref2.itemAction,
        disabled = _ref2.disabled;
    var key = "CBKey:".concat(index);
    return /*#__PURE__*/React.createElement(ListItem, {
      disableRipple: true,
      button: true,
      alignItems: "center",
      "aria-label": value,
      onClick: itemAction,
      key: key,
      sx: styles[comboButtonClassNames.listItem],
      disabled: disabled,
      onBlur: handleBlur,
      onKeyDown: function onKeyDown(event) {
        return handleKeyDown(event, index);
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, value));
  }));
  var trigger = /*#__PURE__*/React.createElement(Button, _extends({
    ref: buttonRef
  }, mergedButtonProps), text);
  var popperProps = {
    handleClickAway: handleClickAway,
    menu: menu,
    sxPaper: comboButtonClassNames.menuPaper,
    open: open,
    popperAnchorRef: buttonRef,
    sx: [comboButtonClassNames.popper, sxPopper],
    popperPosition: popperPosition,
    trigger: trigger
  };
  return /*#__PURE__*/React.createElement(Popper, popperProps);
};

ComboButton.defaultProps = {
  popperPosition: 'bottom-end'
};
ComboButton.displayName = 'ComboButton'; // Tab (while the last list item is focused) - Moves the focus to the first list item
// Shift + Tab (while the first list item is focused) - Moves the focus to the last list item
// Esc (while any list item is focused) - Closes the menu and moves the focus to the button

export { ComboButton };